import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  billingFrequencyMsg,
  contractDetailsMsg,
  durationOfContractMsg,
  idNumberMsg,
  listPriceMsg,
  monthsMsg,
  serviceMsg,
  statusMsg,
  t,
} from '../../common/i18n/index.js';
import { getRecurringPriceAsText } from '../../common/utils/priceUtils.js';
import { getSubscriptionStatus, isYttSubscription } from '../../common/utils/subscriptionUtils.js';
import { getCategoryBySubscriptionType } from '../../public/common/util/category.js';
import { DetailsWithInfoColumn } from '../DetailsWithInfoColumn/DetailsWithInfoColumn.js';
import { getCommitmentText } from '../EoeSubscriptionDetails/eppSubscriptionUtils.js';
import { SubscriptionDetailsLinkBlocks, SubscriptionDetailsLinkType } from './SubscriptionDetailsLinkBlocks.js';
import { YttInstructionsCard } from './YttInstructions.js';
import { getSubscriptionStatusFieldCL } from './subscriptionDetailsCommon.js';
import { getAdminUserAccordionContent, getLicensePricePerUnit } from './subscriptionDetailsSoftwareProduct.js';
import type { CLDescriptionItem } from '../../common/types/componentLibrary.js';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';
import type { Subscription } from '../../generated/api/subscription.js';

const getLinkTypes = (subscription: Subscription) =>
  isYttSubscription(subscription.subscriptionSubType)
    ? [SubscriptionDetailsLinkType.SUPPORT_REQUEST, SubscriptionDetailsLinkType.YTT_MANAGEMENT_SERVICE]
    : [SubscriptionDetailsLinkType.SUPPORT_REQUEST];

interface BasicDetailsProps {
  companyInfo: CompanyInfoResponse;
  hasPendingActions: boolean;
  subscription: Subscription;
}

const BasicDetails = ({ companyInfo, hasPendingActions, subscription }: BasicDetailsProps) => (
  <CL.Grid>
    <CL.GridRow>
      <CL.GridCol className={dsClass.PADDING_LEFT_0} colsS={6} colsM={6} colsL={6}>
        <CL.Description
          className={dsClass.MARGIN_0}
          columns={1}
          items={[
            getSubscriptionStatusFieldCL(getSubscriptionStatus(subscription, hasPendingActions), t.ASQT(statusMsg)),
            { title: t.YTH3(idNumberMsg), description: subscription.subscriptionDisplayId ?? '—' },
          ]}
        />
      </CL.GridCol>
      <CL.GridCol className={dsClass.PADDING_RIGHT_0} colsS={6} colsM={6} colsL={6}>
        <SubscriptionDetailsLinkBlocks
          linkTypes={getLinkTypes(subscription)}
          companyInfo={companyInfo}
          subscription={subscription}
          hasPendingActions={hasPendingActions}
          category={getCategoryBySubscriptionType(subscription.subscriptionType)}
        />
      </CL.GridCol>
    </CL.GridRow>
  </CL.Grid>
);

const getContractDetailsItems = (subscription: Subscription): CLDescriptionItem[] => {
  const { billingPeriod, monthlyRecurringCharge, softwareProduct } = subscription.details ?? {};
  const serviceName = [subscription.subscriptionName, softwareProduct?.editionName].filter(Boolean).join(', ');
  const unitPrice = getLicensePricePerUnit(softwareProduct);

  const items: CLDescriptionItem[] = [
    { title: t.CDME(serviceMsg), description: serviceName || '—' },
    { title: t.CAA9(durationOfContractMsg), description: getCommitmentText(subscription) },
  ];
  if (unitPrice) {
    items.push({ title: t.R39E('Unit price'), description: unitPrice });
  }
  items.push({
    title: t.N68W(listPriceMsg),
    description: getRecurringPriceAsText(monthlyRecurringCharge, billingPeriod),
  });
  if (billingPeriod) {
    items.push({ title: t.PE9O(billingFrequencyMsg), description: `${billingPeriod} ${t.XXVX(monthsMsg)}` });
  }
  if (softwareProduct?.domain) {
    items.push({ title: t.ES5D('Domain name'), description: softwareProduct.domain });
  }
  return items;
};

const SectionHeading = ({ children }: { children: string }) => (
  <h3 className={classNames(dsClass.H3_BOOK, dsClass.MARGIN_BOTTOM_7)}>{children}</h3>
);

export interface ServiceSubscriptionBasicDetailsProps {
  companyInfo: CompanyInfoResponse;
  hasPendingActions: boolean;
  subscription: Subscription;
}

export const ServiceSubscriptionBasicDetails = ({
  companyInfo,
  hasPendingActions,
  subscription,
}: ServiceSubscriptionBasicDetailsProps) => {
  const softwareProduct = subscription.details?.softwareProduct ?? {};

  return (
    <DetailsWithInfoColumn infoColumnContent={<YttInstructionsCard subscription={subscription} />}>
      <BasicDetails companyInfo={companyInfo} hasPendingActions={hasPendingActions} subscription={subscription} />
      <div className="section-separator" />
      <SectionHeading>{t.KJ47(contractDetailsMsg)}</SectionHeading>
      <CL.Description className={dsClass.MARGIN_0} items={getContractDetailsItems(subscription)} />
      {softwareProduct.adminUserEmail && (
        <>
          <div className="section-separator" />
          <SectionHeading>{t.E112('Administrator')}</SectionHeading>
          {getAdminUserAccordionContent(softwareProduct, subscription.subscriptionContactName)}
        </>
      )}
    </DetailsWithInfoColumn>
  );
};
